import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, extractError } from "../lib/api";
import { useAuth, isAdmin } from "../context/AuthContext";
import { PageHeader, SectionCard, Pill, StatePill } from "../components/Ui";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { useWS } from "../context/WebSocketContext";

export default function ProductionBatchesPage() {
  const { user } = useAuth();
  const { subscribe } = useWS();
  const [batches, setBatches] = useState([]);
  const [crops, setCrops] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [form, setForm] = useState({ crop_id: "", type_id: "", notes: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, c, t] = await Promise.all([
        api.get("/production/batches"),
        api.get("/production/masters/crops"),
        api.get("/production/masters/rice-types"),
      ]);
      setBatches(b.data.results || []);
      setCrops(c.data.results || []);
      setTypes(t.data.results || []);
      if (!form.crop_id && c.data.results?.[0]) {
        setForm((f) => ({ ...f, crop_id: String(c.data.results[0].id) }));
      }
      if (!form.type_id && t.data.results?.[0]) {
        setForm((f) => ({ ...f, type_id: String(t.data.results[0].id) }));
      }
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => subscribe("production_batch", load), [subscribe, load]);

  if (!isAdmin(user)) return <Navigate to="/" replace />;

  const plan = async (e) => {
    e.preventDefault();
    if (!form.crop_id || !form.type_id) return toast.error("Pick crop and rice type");
    try {
      await api.post("/production/batches", {
        crop_id: Number(form.crop_id),
        type_id: Number(form.type_id),
        notes: form.notes,
      });
      toast.success("Batch planned");
      setForm((f) => ({ ...f, notes: "" }));
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const start = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/production/batches/${id}/start`);
      toast.success("Paddy unloading started for this batch");
      load();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusyId(null);
    }
  };

  const stopUnloading = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/production/batches/${id}/stop-unloading`);
      toast.success("Stopped paddy unloading — batch is milling-only");
      load();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusyId(null);
    }
  };

  const archive = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/production/batches/${id}/close`);
      toast.success("Batch archived");
      load();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusyId(null);
    }
  };

  const unloading = batches.find((b) => b.status === "UNLOADING_ACTIVE");
  const milling = batches.filter((b) => b.status === "MILLING_ONLY");

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="production-batches-page">
      <PageHeader
        eyebrow="Administration · Production"
        title="Batch controller"
        description="Only one batch can be UNLOADING_ACTIVE. Stop unloading to free the slot; milling-only batches can still receive packaging."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline">
              <Link to="/production/packing-register">Packing register</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/production/batch-masters">Classification masters</Link>
            </Button>
          </div>
        }
      />

      {unloading ? (
        <SectionCard title="Currently unloading paddy" testId="active-batch-banner">
          <div className="flex flex-wrap items-center gap-3">
            <p className="font-mono font-semibold text-base">{unloading.code}</p>
            <Pill>{unloading.crop_name}</Pill>
            <Pill>{unloading.type_name}</Pill>
            <StatePill state={unloading.status} />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busyId === unloading.id}
              onClick={() => stopUnloading(unloading.id)}
            >
              Stop paddy unloading
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={busyId === unloading.id}
              onClick={() => archive(unloading.id)}
            >
              Archive
            </Button>
          </div>
        </SectionCard>
      ) : (
        <SectionCard title="No UNLOADING_ACTIVE batch">
          <p className="text-sm text-muted-foreground">
            Start unloading on a planned batch. Purchases entering unloading attach to that batch.
            {milling.length > 0
              ? ` ${milling.length} batch(es) are still milling and can receive packaging.`
              : ""}
          </p>
        </SectionCard>
      )}

      <SectionCard title="Plan new batch">
        <form onSubmit={plan} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Crop</Label>
            <Select value={form.crop_id} onValueChange={(v) => setForm({ ...form, crop_id: v })}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Select crop" /></SelectTrigger>
              <SelectContent>
                {crops.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>{c.name} ({c.code})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Rice type</Label>
            <Select value={form.type_id} onValueChange={(v) => setForm({ ...form, type_id: v })}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>
                {types.map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>{t.name} ({t.code})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" rows={2} />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <Button type="submit" className="bg-bansal-sage hover:bg-bansal-sageDark text-white min-h-10">
              Plan new batch
            </Button>
          </div>
        </form>
      </SectionCard>

      <SectionCard title="All batches">
        {loading ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
        ) : batches.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No batches yet.</p>
        ) : (
          <div className="space-y-3">
            {batches.map((b) => (
              <div
                key={b.id}
                className={`rounded-lg border p-4 flex flex-wrap items-center justify-between gap-3 ${
                  b.status === "UNLOADING_ACTIVE"
                    ? "border-bansal-sage bg-bansal-sage/5"
                    : b.status === "MILLING_ONLY"
                      ? "border-amber-600/40 bg-amber-50/50"
                      : "border-border bg-card"
                }`}
              >
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-mono font-semibold">{b.code}</p>
                    <StatePill state={b.status} />
                  </div>
                  <p className="text-sm">{b.crop_name} · {b.type_name}</p>
                  {b.notes ? <p className="text-xs text-muted-foreground">{b.notes}</p> : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  {(b.status === "PLANNED" || b.status === "MILLING_ONLY") && (
                    <Button
                      type="button"
                      size="sm"
                      disabled={busyId === b.id}
                      onClick={() => start(b.id)}
                      className="bg-bansal-sage hover:bg-bansal-sageDark text-white"
                    >
                      {b.status === "MILLING_ONLY" ? "Resume unloading" : "Start unloading"}
                    </Button>
                  )}
                  {b.status === "UNLOADING_ACTIVE" && (
                    <Button type="button" size="sm" variant="outline" disabled={busyId === b.id} onClick={() => stopUnloading(b.id)}>
                      Stop paddy unloading
                    </Button>
                  )}
                  {(b.status === "UNLOADING_ACTIVE" || b.status === "MILLING_ONLY") && (
                    <Button type="button" size="sm" variant="ghost" disabled={busyId === b.id} onClick={() => archive(b.id)}>
                      Archive
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
